import { api } from './api';
import {
  RevOpsBrief,
  BriefFeedback,
  BriefComment,
  SubmitLikePayload,
  SubmitCommentPayload,
  NotesResponse,
} from '../Models/RevOpsBrief';

export const revOpsBriefApi = api.injectEndpoints({
  endpoints: (build) => ({
    getLatestBrief: build.query<RevOpsBrief | null, void | string>({
      query: () => ({ url: 'revops-brief/latest', method: 'GET' }),
      transformResponse: normalizeBrief,
      providesTags: ['RevOpsBrief'],
    }),
    getFeedback: build.query<BriefFeedback, string>({
      query: (date) => ({ url: `revops-brief/feedback?date=${encodeURIComponent(date.split("|").pop() ?? date)}`, method: 'GET' }),
      providesTags: ['RevOpsBriefFeedback'],
    }),
    submitLike: build.mutation<void, SubmitLikePayload>({
      invalidatesTags: ['RevOpsBriefFeedback'],
      query: (body) => ({ url: 'revops-brief/feedback/like', method: 'POST', body }),
      async onQueryStarted({ item_id, liked, brief_date, section }, { dispatch, getState, queryFulfilled }) {
        const patches = revOpsBriefApi.util.selectInvalidatedBy(getState(), ['RevOpsBriefFeedback'])
          .filter(entry => entry.endpointName === 'getFeedback' && String(entry.originalArgs).split('|').pop() === brief_date)
          .map(entry => dispatch(
          revOpsBriefApi.util.updateQueryData('getFeedback', entry.originalArgs, (draft) => {
            const existing = draft.likes.find((l) => l.item_id === item_id);
            if (existing) {
              existing.liked = liked;
            } else {
              draft.likes.push({ item_id, liked, section });
            }
          })
        ));
        try {
          await queryFulfilled;
        } catch {
          patches.forEach(patch => patch.undo());
        }
      },
    }),
    submitComment: build.mutation<BriefComment, SubmitCommentPayload>({
      query: (body) => ({ url: 'revops-brief/feedback/comment', method: 'POST', body }),
      invalidatesTags: ['RevOpsBriefFeedback'],
    }),
    deleteLike: build.mutation<void, { item_id: string; brief_date: string }>({
      invalidatesTags: ['RevOpsBriefFeedback'],
      query: ({ item_id }) => ({ url: `revops-brief/feedback/like/${item_id}`, method: 'DELETE' }),
      async onQueryStarted({ item_id, brief_date }, { dispatch, getState, queryFulfilled }) {
        const patches = revOpsBriefApi.util.selectInvalidatedBy(getState(), ['RevOpsBriefFeedback'])
          .filter(entry => entry.endpointName === 'getFeedback' && String(entry.originalArgs).split('|').pop() === brief_date)
          .map(entry => dispatch(
          revOpsBriefApi.util.updateQueryData('getFeedback', entry.originalArgs, (draft: BriefFeedback) => {
            draft.likes = draft.likes.filter((l) => l.item_id !== item_id);
          })
        ));
        try {
          await queryFulfilled;
        } catch {
          patches.forEach(patch => patch.undo());
        }
      },
    }),
    getNotes: build.query<NotesResponse, { start_date: string; end_date: string }>({
      query: ({ start_date, end_date }) => ({
        url: `revops-brief/feedback/notes?start_date=${start_date}&end_date=${end_date}`,
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useGetLatestBriefQuery,
  useGetFeedbackQuery,
  useSubmitLikeMutation,
  useSubmitCommentMutation,
  useDeleteLikeMutation,  
  useGetNotesQuery,
} = revOpsBriefApi;
export function normalizeBrief(brief: RevOpsBrief | null): RevOpsBrief | null {
 if (!brief) return null;
 return { ...brief, what_changed: brief.what_changed ?? [], risks_brief: brief.risks_brief ?? [], opportunities_brief: brief.opportunities_brief ?? [], recommendations: brief.recommendations ?? [], metric_changes: brief.metric_changes ?? [], risks: brief.risks ?? [], opportunities: brief.opportunities ?? [], recommended_actions: brief.recommended_actions ?? [] };
}
